import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  ChevronDown,
  ChevronUp,
  Trash2,
  Edit2,
  Eye,
} from 'feather-icons-react/build/IconComponents';
import {
  fetchInspectionParametersByPartId,
  softDeleteInspectionQuestion,
  softDeleteInspectionResponse,
} from './store/slice';

const styles = {
  container: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '16px',
    border: '1px solid #e0e0e0',
    maxHeight: '500px',
    overflowY: 'auto',
  },
  message: {
    color: '#666',
    fontSize: '14px',
  },
  error: {
    color: '#d9534f',
    fontSize: '14px',
    fontWeight: 500,
  },
  parameterCard: {
    border: '1px solid #dde2eb',
    borderRadius: '8px',
    padding: '14px 18px',
    marginBottom: '14px',
    backgroundColor: '#fdfdff',
    boxShadow: '0 2px 6px rgba(13, 30, 73, 0.06)',
  },
  titleButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    color: '#0d6efd',
    fontWeight: 600,
    fontSize: '15px',
    cursor: 'pointer',
    marginBottom: '4px',
  },
  parameterMeta: {
    fontSize: '12px',
    color: '#888',
    marginBottom: '6px',
  },
  optionList: {
    listStyleType: 'disc',
    paddingLeft: '20px',
    marginBottom: 0,
  },
  optionItem: {
    fontSize: '13px',
    color: '#444',
    marginBottom: '4px',
  },
  parameterHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    padding: '4px 0',
  },
  parameterInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
  },
  parameterNumber: {
    fontWeight: 600,
    color: '#0d6efd',
    display: 'inline-block',
  },
  toggleButton: {
    border: 'none',
    background: '#e9edf5',
    borderRadius: '50%',
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: '16px',
    color: '#0d6efd',
  },
  parameterActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  secondaryButton: {
    backgroundColor: '#0d6efd',
    color: '#fff',
    border: '1px solid #0d6efd',
    borderRadius: '4px',
    padding: '6px 12px',
    cursor: 'pointer',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  ghostButton: {
    backgroundColor: '#f8f9fa',
    color: '#0d6efd',
    border: '1px solid #d0d5db',
    borderRadius: '4px',
    padding: '6px 12px',
    cursor: 'pointer',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  dangerButton: {
    backgroundColor: '#dc3545',
    color: '#fff',
    border: '1px solid #dc3545',
    borderRadius: '4px',
    padding: '6px 12px',
    cursor: 'pointer',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  dangerSmallButton: {
    backgroundColor: '#dc3545',
    color: '#fff',
    border: '1px solid #dc3545',
    borderRadius: '4px',
    padding: '5px 10px',
    cursor: 'pointer',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  optionsContainer: {
    marginTop: '12px',
    borderTop: '1px solid #e6e6e6',
    paddingTop: '12px',
    backgroundColor: '#fff',
    borderRadius: '6px',
  },
  optionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
    borderBottom: '1px dashed #e0e0e0',
  },
  optionText: {
    fontSize: '13px',
    color: '#444',
    flex: 1,
  },
  optionActions: {
    display: 'flex',
    gap: '8px',
    marginLeft: '12px',
  },
};

const getPersistedId = id => {
  const numericId = Number(id);
  if (!Number.isFinite(numericId) || numericId <= 0) return null;
  if (String(id).startsWith('temp-')) return null;
  return numericId;
};

const ParametersList = ({ resolvedPartId, data, onEditParameter, onEditOption, onParametersLoaded, inspectionPlanMasterId }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [parameters, setParameters] = useState([]);
  const [expandedParameterId, setExpandedParameterId] = useState(null);

  const parseResponses = useCallback(responses => {
    if (!responses) return [];

    let parsed = responses;
    if (typeof responses === 'string') {
      try {
        parsed = JSON.parse(responses);
      } catch (error) {
        parsed = [];
      }
    }

    if (!Array.isArray(parsed)) return [];

    return parsed
      .map(item => {
        if (typeof item === 'string') {
          return { id: null, text: item, response: item, expectedValue: '' };
        }
        if (item && typeof item === 'object') {
          const text = item.response || item.parameter || item.text || '';
          const expectedValue = item.expectedvalue || item.ExpectedValue || item.expectedValue || item.expected || '';
          return {
            id: item.id || null,
            text,
            response: text,
            expectedValue,
          };
        }
        return null;
      })
      .filter(option => option && option.text);
  }, []);

  const fetchParameters = useCallback(async () => {
    // Use inspectionPlanMasterId instead of partId
    const lookupId = Number(inspectionPlanMasterId) || 0;
    const partIdValue = Number(resolvedPartId) || 0;
    
    if (!lookupId) {
      setParameters([]);
      onParametersLoaded?.([]);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await dispatch(fetchInspectionParametersByPartId({ 
        inspectionPlanMasterId: lookupId, 
        partId: partIdValue 
      })).unwrap();
      const safeResult = Array.isArray(result) ? result : [];

      const normalizedParameters = safeResult.map((parameter, index) => ({
        id: parameter?.id ?? `temp-${index}`,
        question: parameter?.question ?? '',
        responses: parseResponses(parameter?.responses),
      }));

      onParametersLoaded?.(safeResult);
      setParameters(normalizedParameters);
    } catch (err) {
      const message =
        typeof err === 'string'
          ? err
          : 'Failed to load parameters. Please try again later.';
      setError(message);
      setParameters([]);
      onParametersLoaded?.([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, inspectionPlanMasterId, resolvedPartId, onParametersLoaded, parseResponses]);

  useEffect(() => {
    fetchParameters();
  }, [fetchParameters]);

  useEffect(() => {
    if (!Array.isArray(data)) return;
    const normalized = data.map((parameter, index) => ({
      id: parameter?.id ?? `temp-${index}`,
      question: parameter?.question ?? '',
      responses: parseResponses(parameter?.responses),
    }));
    setParameters(normalized);
  }, [data, parseResponses]);

  useEffect(() => {
    if (expandedParameterId === null) return;
    if (!parameters.some(parameter => parameter.id === expandedParameterId)) {
      setExpandedParameterId(null);
    }
  }, [parameters, expandedParameterId]);

  const handleToggle = parameterId => {
    setExpandedParameterId(prev => (prev === parameterId ? null : parameterId));
  };

  const handleDeleteParameter = async parameterId => {
    const persistedId = getPersistedId(parameterId);
    if (!persistedId) {
      onEditParameter?.(parameterId);
      return;
    }

    try {
      await dispatch(softDeleteInspectionQuestion(persistedId)).unwrap();
      await fetchParameters();
    } catch (err) {
      const message =
        typeof err === 'string'
          ? err
          : 'Failed to delete parameter. Please try again later.';
      setError(message);
    }
  };

  const handleDeleteOption = async (responseId, parameterId) => {
    const persistedId = getPersistedId(responseId);
    if (!persistedId) {
      onEditParameter?.(parameterId);
      return;
    }
    try {
      await dispatch(softDeleteInspectionResponse(persistedId)).unwrap();
      await fetchParameters();
    } catch (err) {
      const message =
        typeof err === 'string'
          ? err
          : 'Failed to delete parameter option. Please try again later.';
      setError(message);
    }
  };

  return (
    <div style={styles.container}>
      {loading && <p style={styles.message}>Loading parameters...</p>}
      {!loading && error && <p style={styles.error}>{error}</p>}
      {!loading && !error && parameters.length === 0 && (
        <p style={styles.message}>No parameters found for the selected inspection plan.</p>
      )}
      {!loading && !error && parameters.length > 0 && (
        <div>
          {parameters.map((parameter, index) => {
            const isExpanded = expandedParameterId === parameter.id;
            return (
              <div key={parameter.id} style={styles.parameterCard}>
                <div style={styles.parameterHeader}>
                  <div style={styles.parameterInfo}>
                    <button
                      type="button"
                      style={styles.toggleButton}
                      onClick={() => handleToggle(parameter.id)}
                      title={isExpanded ? 'Collapse parameter options' : 'Expand parameter options'}
                    >
                      {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    <div>
                      <button
                        type="button"
                        style={styles.titleButton}
                        onClick={() => onEditParameter?.(parameter.id)}
                      >
                        <span style={styles.parameterNumber}>#{index + 1}</span>
                        <span>{parameter.question || 'Unnamed Parameter'}</span>
                      </button>
                      <div style={styles.parameterMeta}>
                        {parameter.responses.length} parameter option{parameter.responses.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                  <div style={styles.parameterActions}>
                    <button
                      type="button"
                      style={styles.secondaryButton}
                      onClick={() => onEditParameter?.(parameter.id)}
                    >
                      <Edit2 size={14} />
                      Edit / View
                    </button>
                    <button
                      type="button"
                      style={styles.dangerButton}
                      onClick={() => handleDeleteParameter(parameter.id)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div style={styles.optionsContainer}>
                    {parameter.responses.length === 0 ? (
                      <p style={styles.message}>No parameter options available.</p>
                    ) : (
                      parameter.responses.map((option, optionIndex) => (
                        <div key={option.id ?? `${parameter.id}-${optionIndex}`} style={styles.optionRow}>
                          <span style={styles.optionText}>
                            {optionIndex + 1}. {option.text}
                          </span>
                          {option.expectedValue && (
                            <span style={{ ...styles.optionText, marginLeft: '12px', color: '#666', fontStyle: 'italic' }}>
                              (Expected: {option.expectedValue})
                            </span>
                          )}
                          <div style={styles.optionActions}>
                            <button
                              type="button"
                              style={styles.ghostButton}
                              onClick={() => onEditOption?.(parameter.id, option.id)}
                            >
                              <Eye size={14} />
                              Add / Edit / View
                            </button>
                            {option.id && (
                              <button
                                type="button"
                                style={styles.dangerSmallButton}
                                onClick={() => handleDeleteOption(option.id, parameter.id)}
                              >
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

ParametersList.defaultProps = {
  resolvedPartId: 0,
  data: [],
  onEditParameter: null,
  onEditOption: null,
  onParametersLoaded: null,
  inspectionPlanMasterId: 0,
};

ParametersList.propTypes = {
  resolvedPartId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  data: PropTypes.array,
  onEditParameter: PropTypes.func,
  onEditOption: PropTypes.func,
  onParametersLoaded: PropTypes.func,
  inspectionPlanMasterId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default ParametersList;

